/**
 * 바늘땀 계획(StitchPlan): 0.1mm 정수 좌표 + 명령. 모든 파일 포맷은 이것만 보고 쓴다.
 *  - 좌표는 디자인 중심이 (0,0) 이 되게 옮긴다 (자수기는 보통 시작점 = 틀 중심).
 *  - 0.3mm 미만 땀 제거, 12.1mm(121단위) 넘는 이동은 나눈다(DST 한계; JEF·EXP 127, PEC 2047 보다 작음).
 */
import type { Pt } from "./geom";
import type { Run } from "./route";

export const STITCH = 0,
  JUMP = 1,
  TRIM = 2,
  COLOR = 3,
  END = 4;

export const MAX_STEP = 121; // 0.1mm 단위

export interface StitchPlan {
  /** 0.1mm 단위, y 아래 방향 */
  xs: Int32Array;
  ys: Int32Array;
  cmds: Uint8Array;
  kinds: Uint8Array;
  colors: string[]; // 색 블록별 실 색 (#rrggbb)
  /** 배치 좌표(mm)에서 뺀 중심 — 계획 좌표 = (배치 좌표 - origin) × 10 */
  originMm: { x: number; y: number };
}

export interface PlanStats {
  stitches: number;
  jumps: number;
  trims: number;
  colors: number;
  colorChanges: number;
  widthMm: number;
  heightMm: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  /** 800 spm 기준 예상 봉제 시간(초) */
  seconds: number;
  maxStitchMm: number;
}

/** 조립 입력: 글자(묶음)마다 런 목록. 같은 묶음 안 런 사이는 점프(길면 트림), 묶음 사이는 트림. */
export interface GroupRuns {
  runs: Run[];
  /** 이 묶음 앞에서 색을 바꾼다 */
  colorChange?: boolean;
}

export interface AssembleOpts {
  minStitch: number;
  trimJumpMm: number;
  tieLen: number;
  color: string;
}

import { tieIn, tieOff } from "./route";
import { K_TRAVEL } from "./route";

interface Raw {
  x: number;
  y: number;
  cmd: number;
  kind: number;
}

export function assemble(groups: GroupRuns[], o: AssembleOpts): StitchPlan {
  const raw: Raw[] = [];
  let last: Pt | null = null;
  let lastRunOpen = false;
  const colors = [o.color];
  const pushRun = (run: Run) => {
    for (let i = 0; i < run.pts.length; i++) raw.push({ x: run.pts[i].x, y: run.pts[i].y, cmd: STITCH, kind: run.kinds[i] });
    last = run.pts[run.pts.length - 1];
  };
  groups.forEach((g, gi) => {
    g.runs.forEach((run0, ri) => {
      if (!run0.pts.length) return;
      const first = gi === 0 && ri === 0;
      if (!first && lastRunOpen) {
        // 이전 런 끝에 tie-off 는 이미 붙였다. 트림 여부
        const d = last ? Math.hypot(run0.pts[0].x - last.x, run0.pts[0].y - last.y) : Infinity;
        const trim = ri === 0 || d > o.trimJumpMm;
        if (trim) raw.push({ x: last!.x, y: last!.y, cmd: TRIM, kind: K_TRAVEL });
        if (ri === 0 && g.colorChange) {
          raw.push({ x: last!.x, y: last!.y, cmd: COLOR, kind: K_TRAVEL });
          colors.push(o.color);
        }
      }
      raw.push({ x: run0.pts[0].x, y: run0.pts[0].y, cmd: JUMP, kind: K_TRAVEL });
      const withTies = tieOff(tieIn(run0, o.tieLen), o.tieLen);
      // tieIn 의 첫 점(=착지점)은 점프가 이미 옮겼으므로 뺀다
      pushRun({ pts: withTies.pts.slice(1), kinds: withTies.kinds.slice(1) });
      lastRunOpen = true;
    });
  });
  return normalize(raw, o.minStitch, colors);
}

function normalize(raw: Raw[], minStitch: number, colors: string[]): StitchPlan {
  // 중심 맞추기 (바늘땀 기준)
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const r of raw) {
    if (r.cmd !== STITCH) continue;
    if (r.x < x0) x0 = r.x;
    if (r.y < y0) y0 = r.y;
    if (r.x > x1) x1 = r.x;
    if (r.y > y1) y1 = r.y;
  }
  if (!isFinite(x0)) x0 = y0 = x1 = y1 = 0;
  const cx = (x0 + x1) / 2,
    cy = (y0 + y1) / 2;
  const min = Math.round(minStitch * 10);
  const xs: number[] = [],
    ys: number[] = [],
    cmds: number[] = [],
    kinds: number[] = [];
  let px = 0,
    py = 0; // 현재 바늘 위치 (원점에서 시작)
  const push = (x: number, y: number, c: number, k: number) => {
    xs.push(x);
    ys.push(y);
    cmds.push(c);
    kinds.push(k);
  };
  let pendingJump: { x: number; y: number } | null = null;
  for (const r of raw) {
    const x = Math.round((r.x - cx) * 10),
      y = Math.round((r.y - cy) * 10);
    if (r.cmd === JUMP) {
      pendingJump = { x, y };
      continue;
    }
    if (r.cmd === TRIM || r.cmd === COLOR) {
      pendingJump = null;
      push(px, py, r.cmd, r.kind);
      continue;
    }
    // STITCH
    if (pendingJump) {
      // 점프: 121 단위 이하로 나눈다
      const dx = pendingJump.x - px,
        dy = pendingJump.y - py;
      const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / MAX_STEP));
      if (dx !== 0 || dy !== 0) {
        for (let i = 1; i <= steps; i++) {
          const jx = px + Math.round((dx * i) / steps),
            jy = py + Math.round((dy * i) / steps);
          push(jx, jy, JUMP, K_TRAVEL);
        }
      }
      px = pendingJump.x;
      py = pendingJump.y;
      pendingJump = null;
    }
    const dx = x - px,
      dy = y - py;
    const d = Math.hypot(dx, dy);
    if (d < min) continue; // 0.3mm 미만 땀 제거
    const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / MAX_STEP));
    for (let i = 1; i <= steps; i++) push(px + Math.round((dx * i) / steps), py + Math.round((dy * i) / steps), STITCH, r.kind);
    px = x;
    py = y;
  }
  push(px, py, END, K_TRAVEL);
  return {
    xs: Int32Array.from(xs),
    ys: Int32Array.from(ys),
    cmds: Uint8Array.from(cmds),
    kinds: Uint8Array.from(kinds),
    colors,
    originMm: { x: cx, y: cy },
  };
}

export function planStats(p: StitchPlan): PlanStats {
  let stitches = 0,
    jumps = 0,
    trims = 0,
    colorChanges = 0;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  let px = 0,
    py = 0,
    maxStep = 0;
  for (let i = 0; i < p.cmds.length; i++) {
    const c = p.cmds[i];
    const x = p.xs[i],
      y = p.ys[i];
    if (c === STITCH) {
      stitches++;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      const d = Math.hypot(x - px, y - py);
      if (d > maxStep && i > 0 && p.cmds[i - 1] === STITCH) maxStep = d;
    } else if (c === JUMP) jumps++;
    else if (c === TRIM) trims++;
    else if (c === COLOR) colorChanges++;
    px = x;
    py = y;
  }
  if (!isFinite(minX)) minX = minY = maxX = maxY = 0;
  // 800 spm, 트림 1회 약 4초, 점프 기록 1개 = 한 땀 시간, 색 바꿈 15초
  const seconds = ((stitches + jumps) / 800) * 60 + trims * 4 + colorChanges * 15;
  return {
    stitches,
    jumps,
    trims,
    colors: p.colors.length,
    colorChanges,
    widthMm: (maxX - minX) / 10,
    heightMm: (maxY - minY) / 10,
    minX,
    maxX,
    minY,
    maxY,
    seconds,
    maxStitchMm: maxStep / 10,
  };
}

/** 계획을 다른 위치로 옮긴 사본 (테스트 시트 배치용) */
export function translatePlan(p: StitchPlan, dx: number, dy: number): StitchPlan {
  return { ...p, xs: p.xs.map((v) => v + dx), ys: p.ys.map((v) => v + dy) };
}
